import { settingsPath } from '../services/platform.js';
import { html } from '../lib/html.js';
import { busy, toast, toastError, toastSuccess } from '../lib/ui.js';
import { tabbarFooter } from '../lib/tabs.js';
import { hsArrows } from '../lib/hscroll.js';
import { openSpaceSheet, spaceButton } from '../lib/space-ui.js';
import { me } from '../services/me.js';
import { CLAIM_STATUS, RELATION, categoryOf, meterOf, vehicleId, vehicleName, vehicles } from '../services/vehicles.js';
import { fmtDay, fmtKm } from '../services/maintenances.js';
import { location } from '../services/location.js';
import { fmtDistance, fmtRating, phoneLink, showcase, whatsappLink } from '../services/showcase.js';
import { Page, go } from './base.js';
import { dismissSetup, pill, sectionTitle, setupCard, setupLater } from './maintenance-ui.js';
import { verifiedTag } from './showcase-ui.js';
import { catIcon } from './vehicle-hero.js';
import { push } from '../services/push.js';
import { reviews } from '../services/reviews.js';
import { accessRequests } from '../services/authorizations.js';

/**
 * Início do espaço Pessoal, de cima para baixo:
 *   seletor de espaço · saudação + resumo da garagem ·
 *   "Para fazer agora" (lembretes, convites, revisão mais urgente, avaliação pendente) ·
 *   garagem em carrossel (com a linha de cada veículo) · empresas perto de você.
 * A lista completa fica na aba Garagem; "Sua empresa" fica em Conta. Puxar para baixo recarrega.
 */
class HomePage extends Page {
  template() {
    return html`
      <ion-content class="ion-padding tc-page root-page has-tabbar" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow body"></div>
      </ion-content>
      ${tabbarFooter('home')}`;
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      if (e.target.closest('[data-action="space"]')) return openSpaceSheet();
      const s = e.target.closest('[data-action^="setup-"]');
      if (s) {
        if (s.dataset.action === 'setup-start') return go(`/vehicles/${s.dataset.vid}/lembretes`);
        dismissSetup(s.dataset.vid);
        return this.renderBody();
      }
      const inv = e.target.closest('[data-invite]');
      if (inv) return this.answerInvite(inv.dataset.invite, inv.dataset.id, inv);
      const p = e.target.closest('[data-push]');
      if (p) return this.pushAction(p.dataset.push);
      const t = e.target.closest('[data-track]');
      if (t) showcase.track(t.dataset.track, t.dataset.type); // o link segue normalmente
      if (e.target.closest('[data-action="nearby-retry"]')) {
        this.nearby = null;
        this.renderBody();
        return this.loadNearby({ fresh: true });
      }
      const el = e.target.closest('[data-go]');
      if (el && !el.hasAttribute('disabled')) go(el.dataset.go);
    });
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      try {
        await me.load({ force: true });
        // puxar para atualizar também mede a localização de novo
        await Promise.all([this.loadGarage(), this.loadNearby({ fresh: true })]);
      } catch (err) {
        toastError(err.message);
      } finally {
        e.target.complete();
      }
    });
  }

  onEnter() {
    this.$('tc-tabbar')?.refresh();
    this.renderBody();
    this.loadGarage();
    this.loadNearby();
    this.checkPush();
  }

  /** Push: registra o aparelho (se já tem permissão) e decide se mostra o cartão "Ativar lembretes". */
  async checkPush() {
    const user = me.current?.user;
    if (!push.supported || !user) return;
    this.pushPerm = await push.permission().catch(() => 'unsupported');
    await push.sync(user).catch(() => {});
    this.renderBody();
  }

  /** Convite para usar o veículo de outra pessoa (pessoa autorizada). */
  async answerInvite(answer, id, el) {
    await busy(el, async () => {
      try {
        if (answer === 'accept') {
          const r = await vehicles.acceptInvite(id);
          toastSuccess('Pronto! O veículo está na sua garagem.');
          this.invites = (this.invites ?? []).filter((x) => x.id !== id);
          return go(`/vehicles/${r.vehicleId}`);
        }
        await vehicles.declineInvite(id);
        this.invites = (this.invites ?? []).filter((x) => x.id !== id);
        toast('Convite recusado.');
      } catch (err) {
        toastError(err.message);
      }
      await this.loadGarage();
    });
  }

  async pushAction(action) {
    if (action === 'later') push.dismissAsk();
    if (action === 'enable') {
      const perm = await push.enable().catch(() => 'denied');
      if (perm !== 'granted') {
        push.dismissAsk();
        toastError(`Notificações bloqueadas. Você pode ativar depois em ${settingsPath('notifications')}.`);
      }
    }
    this.pushPerm = await push.permission().catch(() => 'unsupported');
    this.renderBody();
  }

  async loadGarage() {
    reviews.pending().then((r) => ((this.toReview = r.items), this.renderBody())).catch(() => {});
    vehicles.invites().then((r) => ((this.invites = r.items), this.renderBody())).catch(() => {});
    accessRequests.received().then((r) => ((this.requests = r.items), this.renderBody())).catch(() => {});
    try {
      this.garage = (await vehicles.list()).items;
      this.garageError = null;
    } catch (err) {
      this.garageError = err.message;
    }
    this.renderBody();
  }

  /** Empresas perto: só busca sozinho se a permissão já foi dada (o pedido acontece na busca). */
  async loadNearby({ fresh = false } = {}) {
    const perm = await location.permission().catch(() => 'unsupported');
    if (perm !== 'granted') {
      this.nearby = { state: 'ask', perm };
    } else {
      try {
        const pos = await location.approximate({ fresh });
        this.nearby = { state: 'ok', items: (await showcase.search({ ...pos, limit: 5 })).items };
      } catch (err) {
        // permissão dada, mas a posição ou a busca falhou: mostra o motivo e "Tentar de novo" (antes virava o cartão de pedir permissão)
        this.nearby = { state: 'error', err, perm };
      }
    }
    this.renderBody();
  }

  renderBody() {
    const user = me.current?.user;
    if (!user) return;
    const firstName = user.name.split(' ')[0];
    const todo = [
      this.pushPerm === 'prompt' && user.pushEnabled !== false && !push.askDismissed ? pushCard() : '',
      requestsCard(this.requests),
      ...inviteCards(this.invites),
      setupHome(this.garage),
      alertCard(this.garage),
      reviewCard(this.toReview),
    ].filter(Boolean);
    this.$('.body').innerHTML = html`
      <header class="home-head">
        <div class="top-row">${spaceButton()}</div>
        <h1 class="page-title">${hello()}, ${firstName}</h1>
        ${garageLine(this.garage)}
      </header>
      ${todo.length ? html`${sectionTitle('Para fazer agora', html`<span class="sec-count">${todo.length}</span>`)}${todo}` : ''}
      ${garageSection(this.garage, this.garageError)}
      ${nearbySection(this.nearby)}`.value;
  }
}

function hello() {
  const h = Number(new Intl.DateTimeFormat('pt-BR', { hour: 'numeric', hourCycle: 'h23', timeZone: 'America/Sao_Paulo' }).format(new Date()));
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

const carLabel = ({ vehicle: v, access }) => access?.nickname || vehicleName(v) || vehicleId(v);

/** "3 veículos · 1 precisa de atenção" */
function garageLine(items) {
  if (!items?.length) return '';
  const need = items.filter((x) => ['OVERDUE', 'SOON'].includes(x.summary?.next?.status)).length;
  return html`<p class="page-sub">${items.length === 1 ? '1 veículo' : `${items.length} veículos`}${need ? ` · ${need === 1 ? '1 precisa' : `${need} precisam`} de atenção` : ' · tudo em dia'}</p>`;
}

/** Convite para ativar as notificações (antes do pedido de permissão do aparelho). */
const pushCard = () => html`
  <div class="todo-card">
    <div class="todo-head">
      <span class="row-icon"><ion-icon name="notifications-outline"></ion-icon></span>
      <div class="row-main">
        <span class="todo-title">Ative os lembretes no celular</span>
        <span class="todo-sub">Avisamos quando uma revisão estiver chegando, quando uma empresa registrar um serviço e quando seu documento for analisado.</span>
      </div>
    </div>
    <div class="todo-actions">
      <ion-button class="grow" data-push="enable">Ativar</ion-button>
      <ion-button fill="clear" color="medium" data-push="later">Agora não</ion-button>
    </div>
  </div>`;

/** "Ative os lembretes de revisão" do primeiro veículo que ainda falta (um por vez). */
function setupHome(garage) {
  const it = (garage ?? []).find((x) => x.setupAvailable && !setupLater(x.vehicle.id));
  return it ? setupCard({ vid: it.vehicle.id, car: carLabel(it) }) : '';
}

/** Revisão mais urgente da garagem (vencida ou chegando), com o atalho para a empresa que fez o último serviço. */
function alertCard(garage) {
  const rank = { OVERDUE: 0, SOON: 1 };
  const item = (garage ?? [])
    .filter((x) => x.summary?.next && x.summary.next.status in rank)
    .sort((a, b) => rank[a.summary.next.status] - rank[b.summary.next.status] || (a.summary.next.kmLeft ?? 1e9) - (b.summary.next.kmLeft ?? 1e9))[0];
  if (!item) return '';
  const { next, lastWorkshop: w } = item.summary;
  const unit = meterOf(item.vehicle);
  const cat = categoryOf(item.vehicle);
  const car = carLabel(item);
  const overdue = next.status === 'OVERDUE';
  const when = overdue ? 'está vencida' : next.kmLeft !== null && next.kmLeft > 0 ? `vence em ${fmtKm(next.kmLeft, unit)}` : next.nextDueDate ? `vence em ${fmtDay(next.nextDueDate)}` : 'está chegando';
  const msg = `Olá! Vi no TicketCar que a ${next.typeName.toLowerCase()} do meu ${vehicleName(item.vehicle) || car} ${overdue ? 'está vencida' : 'está chegando'}. Podemos agendar?`;
  const contact = w?.whatsapp
    ? html`<ion-button href="${whatsappLink(w.whatsapp, msg)}" target="_blank" class="grow" ${w.listed ? html`data-track="${w.id}" data-type="whatsapp"` : ''}><ion-icon slot="start" name="logo-whatsapp"></ion-icon>Agendar no WhatsApp</ion-button>`
    : w?.phone
      ? html`<ion-button href="${phoneLink(w.phone)}" class="grow" ${w.listed ? html`data-track="${w.id}" data-type="call"` : ''}><ion-icon slot="start" name="call-outline"></ion-icon>Ligar e agendar</ion-button>`
      : html`<ion-button class="grow" data-go="/oficinas"><ion-icon slot="start" name="search-outline"></ion-icon>Achar uma empresa</ion-button>`;
  return html`
    <section class="todo-card ${overdue ? 'danger' : 'warning'}">
      <div class="todo-head">
        <span class="row-icon ${overdue ? 'danger' : 'warning'}"><ion-icon name="${overdue ? 'alert-circle-outline' : 'time-outline'}"></ion-icon></span>
        <div class="row-main">
          <span class="todo-over cat-color-${cat.key}"><ion-icon name="${cat.icon}" aria-hidden="true"></ion-icon>${car}</span>
          <span class="todo-title">${next.typeName} ${when}</span>
          <span class="todo-sub">Última feita ${w ? html`em ${w.name} · ` : 'em '}${fmtDay(next.lastServiceDate)}</span>
        </div>
      </div>
      <div class="todo-actions">
        ${contact}
        <ion-button fill="outline" color="medium" data-go="/vehicles/${item.vehicle.id}">Ver</ion-button>
      </div>
    </section>`;
}

/** Empresas que pediram acesso a um veículo meu pela placa: um cartão só, que abre a tela de responder. */
function requestsCard(list) {
  if (!list?.length) return '';
  const [first] = list;
  const car = first.vehicle.nickname || vehicleName(first.vehicle) || vehicleId(first.vehicle) || 'seu veículo';
  return html`
    <button type="button" class="panel row tappable todo-card" data-go="/pedidos-acesso">
      <span class="row-icon warning"><ion-icon name="storefront-outline"></ion-icon></span>
      <div class="row-main">
        <span class="row-title">${list.length === 1 ? `${first.workshop.name} pediu acesso ao ${car}` : `${list.length} empresas pediram acesso aos seus veículos`}</span>
        <span class="row-sub">Toque para escolher o prazo e aceitar, ou recusar.</span>
      </div>
      <ion-icon class="chevron" name="chevron-forward"></ion-icon>
    </button>`;
}

/** Convites para usar o veículo de outra pessoa. */
const inviteCards = (list) =>
  (list ?? []).map((i) => html`
    <div class="todo-card">
      <div class="todo-head">
        <span class="row-icon"><ion-icon name="people-outline"></ion-icon></span>
        <div class="row-main">
          <span class="todo-title">${i.owner.firstName} quer compartilhar o ${i.vehicle.label} com você</span>
          <span class="todo-sub">${i.vehicle.plate ? `Placa ${i.vehicle.plate}. ` : ''}Você vai ver o histórico, receber os lembretes e poder autorizar empresas.</span>
        </div>
      </div>
      <div class="todo-actions">
        <ion-button class="grow" data-invite="accept" data-id="${i.id}">Aceitar</ion-button>
        <ion-button fill="clear" color="medium" data-invite="decline" data-id="${i.id}">Recusar</ion-button>
      </div>
    </div>`);

/** Serviço registrado esperando a nota do cliente (o mais recente). */
function reviewCard(list) {
  const it = list?.[0];
  if (!it) return '';
  return html`
    <button type="button" class="panel row tappable review-ask todo-card" data-go="/avaliar/${it.maintenanceId}">
      <span class="row-icon"><ion-icon name="star-outline"></ion-icon></span>
      <div class="row-main">
        <span class="row-title">Como foi ${it.workshop?.name ? `a ${it.workshop.name}` : 'o serviço'}?</span>
        <span class="row-sub">${it.services.join(', ')} · ${it.vehicle.label}. Leva 20 segundos${list.length > 1 ? ` · mais ${list.length - 1} para avaliar` : ''}.</span>
      </div>
      <ion-icon class="chevron" name="chevron-forward"></ion-icon>
    </button>`;
}

/** Placa no padrão Mercosul (pequena) ou o número de série, para quem não tem placa. */
export const smallId = (v) =>
  v.plateFormatted
    ? html`<span class="plate" aria-label="Placa ${v.plateFormatted}"><span class="plate-top" aria-hidden="true">BRASIL</span><span class="plate-num">${v.plateFormatted.replace('-', '')}</span></span>`
    : v.serialNumber
      ? html`<span class="serial-tag" aria-label="Número de série">${v.serialNumber}</span>`
      : '';

/** Próxima revisão em poucas palavras ("Em 1.200 km", "Vencida", "12/05/2027"). */
export function nextShort(next, unit) {
  if (!next) return { text: 'Sem previsão', cls: '' };
  if (next.status === 'OVERDUE') return { text: 'Vencida', cls: 'danger' };
  const text = next.kmLeft !== null && next.kmLeft > 0 ? fmtKm(next.kmLeft, unit) : next.nextDueDate ? fmtDay(next.nextDueDate) : '—';
  return { text: next.status === 'SOON' ? text : 'Em dia', cls: next.status === 'SOON' ? 'warning' : 'success' };
}

/** Garagem: um cartão por veículo (linha, medidor e próxima revisão) e, no fim, "Adicionar". */
function garageSection(items, error) {
  const title = sectionTitle('Garagem', items?.length ? html`<button type="button" class="link-btn" data-go="/garagem">Ver tudo</button>` : '');
  if (!items && !error) return html`${title}<div class="panel center-small"><ion-spinner></ion-spinner></div>`;
  if (error && !items) return html`${title}<div class="panel"><p class="panel-text">Não foi possível carregar seus veículos. Puxe para baixo para tentar de novo.</p></div>`;
  if (!items.length) {
    return html`
      ${title}
      <div class="panel empty-panel">
        <div class="empty-badge"><ion-icon name="tc-garage"></ion-icon></div>
        <strong>Adicione seu primeiro veículo</strong>
        <p>Carro, caminhão ou máquina agrícola. As empresas registram cada serviço e o app avisa quando a próxima revisão chegar.</p>
        <ion-button expand="block" class="empty-cta" data-go="/vehicles/new"><ion-icon slot="start" name="add-circle-outline"></ion-icon>Adicionar veículo</ion-button>
      </div>`;
  }
  return html`
    ${title}
    <div class="hs-wrap">${hsArrows()}<div class="hscroll">
      ${items.map((it) => {
        const { vehicle: v, access, claim, summary } = it;
        const chip = access ? (access.relation === 'OWNER' ? null : RELATION[access.relation]) : CLAIM_STATUS[claim?.status];
        const nickname = access?.nickname || claim?.submitted?.nickname;
        const model = [v.brand, v.model].filter(Boolean).join(' ');
        const name = nickname || model || vehicleId(v);
        const cat = categoryOf(v);
        const unit = meterOf(v);
        // com apelido: modelo e ano embaixo; sem apelido o título já é o modelo: ano e linha
        const sub = [nickname ? model : '', v.modelYear, cat.key === 'LIGHT' ? '' : cat.label].filter(Boolean).join(' · ');
        const n = nextShort(summary?.next, unit);
        return html`
          <button type="button" class="car-card tappable" data-go="/vehicles/${v.id}">
            <span class="car-top">
              ${catIcon(cat.key)}
              ${smallId(v)}
            </span>
            <span class="row-main">
              <span class="car-title">${name}</span>
              <span class="row-sub one-line">${sub}</span>
            </span>
            ${access
              ? html`
                <span class="car-stats">
                  <span><span class="car-label">${unit === 'HOURS' ? 'Horímetro' : 'Km'}</span><strong>${summary?.currentKm === null || summary?.currentKm === undefined ? '—' : fmtKm(summary.currentKm, unit)}</strong></span>
                  <span><span class="car-label">Revisão</span><strong class="${n.cls}">${n.text}</strong></span>
                </span>`
              : ''}
            ${chip ? html`<span class="car-chip">${pill(chip.color, chip.label)}</span>` : ''}
          </button>`;
      })}
      <button type="button" class="car-card add tappable" data-go="/vehicles/new">
        <ion-icon name="add-circle-outline"></ion-icon>
        <span>Adicionar veículo</span>
      </button>
    </div></div>`;
}

/** "Sua localização há 4 min": quando a posição usada foi medida (puxar a tela para baixo mede de novo). */
function fixInfo() {
  const f = location.lastFix;
  if (!f) return '';
  const min = Math.round(Math.max(0, Date.now() - f.at) / 60_000);
  const when = min < 1 ? 'agora' : min < 60 ? `há ${min} min` : min < 1440 ? `há ${Math.round(min / 60)} h` : 'há mais de um dia';
  return html`
    <div class="near-fix">
      <ion-icon name="location-outline" aria-hidden="true"></ion-icon>
      <span>Sua localização ${when}</span>
    </div>`;
}

/** Carrossel "Perto de você". */
function nearbySection(n) {
  const title = sectionTitle('Perto de você', n?.state === 'ok' && n.items.length ? html`<button type="button" class="link-btn" data-go="/oficinas">Buscar</button>` : '');
  if (!n) return html`${title}<div class="panel center-small"><ion-spinner></ion-spinner></div>`;
  if (n.state === 'ask') {
    return html`
      ${title}
      <button type="button" class="panel row tappable" data-go="/oficinas">
        <span class="row-icon"><ion-icon name="location-outline"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">Encontre empresas credenciadas</span>
          <span class="row-sub">As mais perto, com nota dos clientes e as linhas que atendem.</span>
        </div>
        <ion-icon class="chevron" name="chevron-forward"></ion-icon>
      </button>`;
  }
  if (n.state === 'error') {
    const off = n.err?.code === 'LOCATION_OFF';
    const loc = n.err?.code?.startsWith('LOCATION_');
    return html`
      ${title}
      <div class="panel nearby-error">
        <p class="panel-text"><b>${off ? 'A localização do celular está desligada' : loc ? 'Não conseguimos sua localização agora' : 'Não foi possível buscar as empresas agora'}</b></p>
        <p class="panel-text">${off ? 'Ligue a localização e toque em Tentar de novo.' : 'Verifique a internet e a localização do celular e tente de novo.'}</p>
        <ion-button fill="outline" size="small" data-action="nearby-retry">Tentar de novo</ion-button>
      </div>`;
  }
  if (!n.items.length) {
    return html`${title}${fixInfo()}<div class="panel"><p class="panel-text">Ainda não há empresas do TicketCar perto de você. Conhece uma boa? Indique o app para ela.</p></div>`;
  }
  return html`
    ${title}
    ${fixInfo()}
    <div class="hs-wrap">${hsArrows()}<div class="hscroll">
      ${n.items.map((w) => html`
        <button type="button" class="shop-card tappable" data-go="/oficinas/${w.id}">
          <span class="shop-photo">${w.cover ? html`<img src="${w.cover}" alt="" loading="lazy" />` : html`<ion-icon name="storefront-outline"></ion-icon>`}</span>
          <span class="shop-body">
            <span class="shop-name">${w.name}</span>
            ${w.verified ? verifiedTag() : ''}
            <span class="shop-meta">${w.rating ? html`<ion-icon class="star" name="star"></ion-icon><b>${fmtRating(w.rating.avg)}</b> (${w.rating.count}) · ` : html`<span class="new-tag">Nova no TicketCar</span> · `}${fmtDistance(w.distanceKm)}</span>
          </span>
        </button>`)}
    </div></div>`;
}

customElements.define('page-home', HomePage);
